import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Navbar, Footer } from '../../components';
import { formatCourseThumbnail, handleThumbnailError } from '../../utils/imageUtils';
import api from '../../services/api';
import { getCalendarParts, formatTime } from '../../utils/dateUtils';
import SEO from '../../components/common/SEO';

const StudentCourseDetails = () => {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubscriptionActive, setIsSubscriptionActive] = useState(false);
  const [isEnrolledInCourse, setIsEnrolledInCourse] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [subData, courseData, classData] = await Promise.all([
          api.getMySubscription().catch(() => ({ subscription: null, isActive: false })),
          api.getCourse(courseId).catch(() => ({ course: null })),
          api.getClassesByCourse(courseId).catch(() => ({ classes: [] })),
        ]);

        setIsSubscriptionActive(subData.isActive || false);
        setSubscriptionLoading(false);

        const sub = subData.subscription;
        if (subData.isActive && sub) {
          const allowed = sub.allowedCourses || [];
          const enrolled = allowed.length === 0 || allowed.some(c => (c._id || c).toString() === courseId);
          setIsEnrolledInCourse(enrolled);
        }

        setCourse(courseData.course);
        setClasses((classData.classes || []).filter(c => c.status === 'published'));
      } catch (error) {
        console.error('Error fetching course:', error);
      } finally {
        setLoading(false);
      }
    };

    if (courseId) fetchData();
  }, [courseId]);

  if (!subscriptionLoading && !isSubscriptionActive) {
    return (
      <div className="min-h-screen bg-dark-50">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="card p-8 text-center">
              <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h2 className="text-2xl font-bold text-dark-900 mb-2">Subscription Required</h2>
              <p className="text-dark-500 mb-6">You need an active subscription to access course content and attend classes.</p>
              <Link to="/student/dashboard" className="btn-primary inline-block">Back to Dashboard</Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (loading || subscriptionLoading) {
    return (
      <div className="min-h-screen bg-dark-50">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center py-12 text-dark-500">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500 mx-auto mb-3" />
              Loading course...
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-dark-50">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="card p-8 text-center">
              <h2 className="text-2xl font-bold text-dark-900 mb-2">Course Not Found</h2>
              <p className="text-dark-500 mb-6">The course you're looking for doesn't exist or is no longer available.</p>
              <Link to="/student/dashboard" className="btn-primary inline-block">Back to Dashboard</Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const now = new Date();
  const sortedClasses = [...classes].sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));
  const upcomingClasses = sortedClasses.filter(c => new Date(c.scheduledDate) >= now);
  const pastClasses = sortedClasses.filter(c => new Date(c.scheduledDate) < now);

  return (
    <div className="min-h-screen bg-dark-50">
      <SEO title={course ? `${course.title} — Learning` : 'Course Details'} noIndex />
      <Navbar />

      <main className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-dark-500 mb-6">
            <Link to="/student/dashboard" className="hover:text-primary-500">Dashboard</Link>
            <span>/</span>
            <Link to="/student/courses" className="hover:text-primary-500">My Courses</Link>
            <span>/</span>
            <span className="text-dark-900 line-clamp-1">{course.title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Course Header */}
              <div className="card p-6">
                <div className="flex flex-col md:flex-row gap-6">
                  <img
                    src={formatCourseThumbnail(course.thumbnail)}
                    alt={course.title}
                    className="w-full md:w-48 h-32 object-cover rounded-lg"
                    onError={handleThumbnailError}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h1 className="text-xl md:text-2xl font-bold text-dark-900">{course.title}</h1>
                      {isEnrolledInCourse && (
                        <span className="bg-green-100 text-green-700 text-xs font-medium px-2 py-0.5 rounded-full">Enrolled</span>
                      )}
                    </div>
                    {course.shortDescription && (
                      <p className="text-dark-500 mb-3 line-clamp-2">{course.shortDescription || course.description}</p>
                    )}
                    {course.teacher && (
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-white font-semibold text-sm">
                          {course.teacher.name?.charAt(0) || 'T'}
                        </div>
                        <div>
                          <p className="font-medium text-dark-900 text-sm">{course.teacher.name}</p>
                          <p className="text-xs text-dark-400">Instructor</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Class Schedule - Primary section for students */}
              <div className="card overflow-hidden">
                <div className="p-6 pb-4 flex items-center justify-between">
                  <h2 className="text-lg font-bold text-dark-900">Live Classes</h2>
                  <span className="text-sm text-dark-400">{classes.length} class{classes.length !== 1 ? 'es' : ''}</span>
                </div>

                {classes.length > 0 ? (
                  <>
                    {isEnrolledInCourse && upcomingClasses.length > 0 && (
                      <div className="mx-6 mb-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                        <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm text-green-800">You can join these classes. Click <strong>Join Class</strong> when a class is live.</span>
                      </div>
                    )}

                    {/* Upcoming */}
                    {upcomingClasses.length > 0 && (
                      <div>
                        <div className="px-6 py-2">
                          <p className="text-xs font-semibold text-dark-400 uppercase tracking-wide">Upcoming ({upcomingClasses.length})</p>
                        </div>
                        <div className="divide-y divide-dark-100">
                          {upcomingClasses.map((cls, i) => {
                            const classDate = new Date(cls.scheduledDate);
                            const isNext = i === 0;
                            const isToday = classDate.toDateString() === now.toDateString();
                            const teacherName = cls.teacher && typeof cls.teacher === 'object' ? cls.teacher.name : null;
                            const parts = getCalendarParts(cls.scheduledDate);

                            return (
                              <div key={cls._id} className={`px-6 py-4 flex items-start gap-4 ${isNext ? 'bg-primary-50/50' : 'hover:bg-dark-50'} transition-colors`}>
                                <div className={`flex-shrink-0 w-16 text-center rounded-lg py-2 ${isNext ? 'bg-primary-500 text-white' : 'bg-dark-100 text-dark-600'}`}>
                                  <p className="text-xs font-medium leading-tight">{parts.month}</p>
                                  <p className="text-xl font-bold leading-tight">{parts.day}</p>
                                  <p className="text-xs leading-tight">{parts.weekday}</p>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h3 className="font-semibold text-dark-900">{cls.title}</h3>
                                    {isNext && <span className="text-xs bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded font-medium">Next</span>}
                                    {isToday && (
                                      <span className="flex items-center gap-1 text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-medium">
                                        <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
                                        Today
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-dark-500 mt-0.5">
                                    {formatTime(cls.scheduledDate)}
                                    {cls.duration ? ` · ${cls.duration} min` : ''}
                                    {teacherName ? ` · ${teacherName}` : ''}
                                  </p>
                                  {cls.description && (
                                    <div className="text-sm text-dark-500 mt-1 line-clamp-2" dangerouslySetInnerHTML={{ __html: cls.description }} />
                                  )}
                                </div>
                                {isEnrolledInCourse ? (
                                  cls.meetingLink ? (
                                    <a href={cls.meetingLink} target="_blank" rel="noopener noreferrer"
                                      className={`flex-shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                        isNext ? 'btn-primary' : 'border border-primary-300 text-primary-600 hover:bg-primary-50'
                                      }`}>
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                      </svg>
                                      Join Class
                                    </a>
                                  ) : (
                                    <span className="flex-shrink-0 text-sm text-dark-400 py-2">Link coming soon</span>
                                  )
                                ) : (
                                  <span className="flex-shrink-0 text-xs text-dark-400 py-2">Not enrolled</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Past */}
                    {pastClasses.length > 0 && (
                      <div className="border-t border-dark-100 px-6 py-3">
                        <details className="group">
                          <summary className="text-xs font-medium text-dark-400 cursor-pointer hover:text-dark-600 list-none flex items-center gap-1">
                            <svg className="w-3.5 h-3.5 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            {pastClasses.length} past class{pastClasses.length !== 1 ? 'es' : ''}
                          </summary>
                          <div className="mt-2 divide-y divide-dark-50">
                            {pastClasses.map((cls) => {
                              const parts = getCalendarParts(cls.scheduledDate);
                              return (
                                <div key={cls._id} className="flex items-center gap-4 py-3 text-dark-400">
                                  <span className="w-16 text-center text-xs flex-shrink-0">
                                    {parts.month} {parts.day}
                                  </span>
                                  <span className="text-sm">{cls.title}</span>
                                  <span className="text-xs ml-auto">
                                    {formatTime(cls.scheduledDate)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </details>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="px-6 pb-6 text-center text-sm text-dark-400">
                    No classes scheduled yet for this course.
                  </div>
                )}
              </div>

              {/* About This Course */}
              {(course.aboutCourse || course.description) && (
                <div className="card p-6">
                  <h2 className="text-lg font-bold text-dark-900 mb-3">About This Course</h2>
                  {course.aboutCourse ? (
                    <div className="prose max-w-none text-dark-600" dangerouslySetInnerHTML={{ __html: course.aboutCourse }} />
                  ) : (
                    <p className="text-dark-600 whitespace-pre-line">{course.description}</p>
                  )}
                </div>
              )}

              {/* What You'll Learn */}
              {course.whatYouWillLearn && (
                <div className="card p-6">
                  <h2 className="text-lg font-bold text-dark-900 mb-3">What You'll Learn</h2>
                  <div className="prose max-w-none text-dark-600" dangerouslySetInnerHTML={{ __html: course.whatYouWillLearn }} />
                </div>
              )}

              {/* Prerequisites */}
              {course.prerequisites && (
                <div className="card p-6">
                  <h2 className="text-lg font-bold text-dark-900 mb-3">Prerequisites</h2>
                  <div className="prose max-w-none text-dark-600" dangerouslySetInnerHTML={{ __html: course.prerequisites }} />
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Actions */}
              <div className="card p-6">
                <h3 className="text-lg font-bold text-dark-900 mb-4">Quick Access</h3>
                {upcomingClasses.length > 0 && upcomingClasses[0].meetingLink && isEnrolledInCourse && (
                  <a href={upcomingClasses[0].meetingLink} target="_blank" rel="noopener noreferrer"
                    className="w-full btn-primary flex items-center justify-center gap-2 py-3 mb-3 rounded-lg font-medium">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Join Next Class
                  </a>
                )}
                <Link to="/student/live-classes" className="w-full block text-center border border-primary-300 text-primary-600 hover:bg-primary-50 py-2.5 rounded-lg font-medium text-sm transition-colors">
                  View All Live Classes
                </Link>
              </div>

              {/* Course Info */}
              <div className="card p-6">
                <h3 className="text-lg font-bold text-dark-900 mb-4">Course Info</h3>
                <div className="space-y-3 text-sm">
                  {course.level && (
                    <div className="flex justify-between">
                      <span className="text-dark-500">Level</span>
                      <span className="font-medium text-dark-900 capitalize">{course.level}</span>
                    </div>
                  )}
                  {course.category && (
                    <div className="flex justify-between">
                      <span className="text-dark-500">Category</span>
                      <span className="font-medium text-dark-900">{course.category}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-dark-500">Classes</span>
                    <span className="font-medium text-dark-900">{classes.length}</span>
                  </div>
                  {upcomingClasses.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-dark-500">Upcoming</span>
                      <span className="font-medium text-primary-600">{upcomingClasses.length}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default StudentCourseDetails;
